import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  AppMetaDataItemNames,
  DocumentItemEntity,
  DocumentVersionEntity
} from '../../../../meta-data/app-meta-data.service';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';

@DwComponent({
  key: 'document-version-reference-viewer-form-section',
  name: 'Document Version Reference Viewer',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.DocumentVersion,
  isGlobal: false
})
@Component({
  selector: 'app-document-version-reference-viewer-form-section',
  template: `
    <div *ngIf="!loading && errorMessage" class="dw-form-field">
      <div class="alert alert-warning mb-0">{{ errorMessage }}</div>
    </div>

    <app-document-reference-viewer
      *ngIf="!loading && viewerDocumentItem"
      [documentItem]="viewerDocumentItem"
      [documentVersionId]="viewerDocumentVersionId"
      [hideHeader]="false">
    </app-document-reference-viewer>
  `
})
export class DocumentVersionReferenceViewerFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  formApi: DwMetaDataFormApi;
  loading = false;
  errorMessage?: string;
  viewerDocumentItem?: DocumentItemEntity;
  viewerDocumentVersionId?: number;

  private readonly destroy$ = new Subject<void>();
  private lastDocumentVersionId?: number;
  private lastDocumentItemId?: number;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private documentsRepositoryService: DocumentsRepositoryService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.runAfterFormDataLoaded(() => this.loadViewer());

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.loadViewer());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  private loadViewer(): void {
    const documentVersion = this.formApi.getFormData<DocumentVersionEntity>();
    const documentVersionId = documentVersion?.DocumentVersionId;
    const relatedDocumentItemId = documentVersion?.DocumentPublication?.DocumentItemId;
    const documentPublicationId = documentVersion?.DocumentPublicationId;

    if (!documentVersionId) {
      this.viewerDocumentVersionId = undefined;
      this.viewerDocumentItem = undefined;
      this.errorMessage = 'Document version data is not available yet.';
      this.loading = false;
      return;
    }

    if (this.lastDocumentVersionId === documentVersionId
      && this.lastDocumentItemId === relatedDocumentItemId
      && this.viewerDocumentItem) {
      return;
    }

    this.viewerDocumentVersionId = documentVersionId;
    this.loading = true;
    this.errorMessage = undefined;

    if (relatedDocumentItemId) {
      this.loadDocumentItem(documentVersionId, relatedDocumentItemId);
      return;
    }

    if (!documentPublicationId) {
      this.viewerDocumentItem = undefined;
      this.loading = false;
      this.errorMessage = 'This document version is not linked to a publication.';
      return;
    }

    this.documentsRepositoryService.loadDocumentPublication(documentPublicationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        publication => {
          if (!publication?.DocumentItemId) {
            this.viewerDocumentItem = undefined;
            this.loading = false;
            this.errorMessage = 'Unable to locate the document item for this version.';
            return;
          }

          this.loadDocumentItem(documentVersionId, publication.DocumentItemId);
        },
        () => {
          this.viewerDocumentItem = undefined;
          this.loading = false;
          this.errorMessage = 'Unable to load the document publication for this version.';
        }
      );
  }

  private loadDocumentItem(documentVersionId: number, documentItemId: number): void {
    this.documentsRepositoryService.loadDocumentItem(documentItemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        documentItem => {
          this.viewerDocumentItem = documentItem;
          this.viewerDocumentVersionId = documentVersionId;
          this.lastDocumentVersionId = documentVersionId;
          this.lastDocumentItemId = documentItemId;
          this.loading = false;
        },
        () => {
          this.viewerDocumentItem = undefined;
          this.loading = false;
          this.errorMessage = 'Unable to load the related document item for this version.';
        }
      );
  }
}
